import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import * as Sentry from '@sentry/react'
import Clarity from '@microsoft/clarity'
import { authService } from '@/services'
import type { AuthResponse, LoginRequest, RegisterRequest, TwoFactorChallenge } from '@/types'
import { isTwoFactorChallenge, isEmailVerificationPending, EMAIL_NOT_VERIFIED_CODE } from '@/types'
import { getErrorMessage, getErrorCode } from '@/utils/error'
import { useToast } from './use-toast'

export const useAuth = () => {
  const navigate = useNavigate()
  const { toast } = useToast()
  const queryClient = useQueryClient()

  // Desafio de 2FA pendente (login segurado até o 2º fator)
  const [twoFactorChallenge, setTwoFactorChallenge] = useState<TwoFactorChallenge | null>(null)
  // Login barrado por conta local ainda não ativada por e-mail
  const [emailNotVerified, setEmailNotVerified] = useState(false)

  // Get current user
  const { data: user, isLoading } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: authService.me,
    enabled: authService.isAuthenticated(),
    retry: false,
  })

  // Finaliza a autenticação (compartilhado por login sem-2FA e verify-2FA)
  const onAuthenticated = (data: AuthResponse) => {
    Sentry.setUser({ id: data.user.id })
    if (import.meta.env.PROD) Clarity.identify(data.user.id)
    queryClient.setQueryData(['auth', 'me'], data.user)
    setTwoFactorChallenge(null)
    toast({
      title: 'Login realizado com sucesso!',
      description: `Bem-vindo de volta, ${data.user.name}!`,
    })
    navigate('/home')
  }

  // Login mutation
  const loginMutation = useMutation({
    mutationFn: (credentials: LoginRequest) => authService.login(credentials),
    onSuccess: (data) => {
      // 2FA ativo: segura o login e mostra o passo de código
      if (isTwoFactorChallenge(data)) {
        setTwoFactorChallenge(data)
        return
      }
      onAuthenticated(data)
    },
    onError: (error: unknown) => {
      // Conta local ainda não ativada: mostra painel inline (sem toast genérico)
      if (getErrorCode(error) === EMAIL_NOT_VERIFIED_CODE) {
        setEmailNotVerified(true)
        return
      }
      toast({
        title: 'Erro ao fazer login',
        description: getErrorMessage(error, 'Verifique suas credenciais e tente novamente'),
        variant: 'destructive',
      })
    },
  })

  // Verificação do 2º fator
  const verifyTwoFactorMutation = useMutation({
    mutationFn: ({ challengeId, code }: { challengeId: string; code: string }) =>
      authService.verifyTwoFactor(challengeId, code),
    onSuccess: onAuthenticated,
    onError: (error: unknown) => {
      toast({
        title: 'Código inválido',
        description: getErrorMessage(error, 'Verifique o código e tente novamente'),
        variant: 'destructive',
      })
    },
  })

  // Register mutation
  const registerMutation = useMutation({
    mutationFn: (data: RegisterRequest) => authService.register(data),
    onSuccess: (data) => {
      // Cadastro local cria conta inativa: vai pra tela de "confirme seu e-mail".
      if (isEmailVerificationPending(data)) {
        navigate('/check-email', { state: { email: data.email } })
        return
      }
      queryClient.setQueryData(['auth', 'me'], data.user)
      toast({
        title: 'Cadastro realizado com sucesso!',
        description: `Bem-vindo, ${data.user.name}!`,
      })
      navigate('/home')
    },
    onError: (error: unknown) => {
      toast({
        title: 'Erro ao criar conta',
        description: getErrorMessage(error, 'Tente novamente mais tarde'),
        variant: 'destructive',
      })
    },
  })

  // Logout mutation
  const logoutMutation = useMutation({
    mutationFn: authService.logout,
    onSuccess: () => {
      Sentry.setUser(null)
      queryClient.clear()
      toast({
        title: 'Logout realizado',
        description: 'Até logo!',
      })
      navigate('/auth')
    },
  })

  return {
    user,
    isLoading,
    isAuthenticated: !!user, // Reativo: atualiza quando user muda
    token: authService.isAuthenticated() ? localStorage.getItem('token') : null,
    login: loginMutation.mutate,
    register: registerMutation.mutate,
    logout: logoutMutation.mutate,
    isLoggingIn: loginMutation.isPending,
    isRegistering: registerMutation.isPending,
    isLoggingOut: logoutMutation.isPending,
    // 2FA
    twoFactorChallenge,
    verifyTwoFactor: (code: string) => {
      if (twoFactorChallenge) {
        verifyTwoFactorMutation.mutate({ challengeId: twoFactorChallenge.challengeId, code })
      }
    },
    isVerifyingTwoFactor: verifyTwoFactorMutation.isPending,
    resendTwoFactor: () =>
      twoFactorChallenge && authService.resendTwoFactor(twoFactorChallenge.challengeId),
    cancelTwoFactor: () => setTwoFactorChallenge(null),
    // Ativação por e-mail (login barrado)
    emailNotVerified,
    resendVerification: (email: string) => authService.resendVerification(email),
    clearEmailNotVerified: () => setEmailNotVerified(false),
  }
}
